import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { searchCustomers } from '../services/api';
import { CustomerRef } from '../types';

interface Props {
  // The currently-confirmed/resolved target customer, or null when none is set
  // yet. When present it seeds the input so the field reflects the saved target.
  value: CustomerRef | null;
  // The AI's candidate determinations, shown FIRST as suggestions before the rep
  // types anything (the #7 auto-fill: preload what the AI guessed).
  candidates?: CustomerRef[];
  // Called when the rep confirms/overrides a customer from the list.
  onSelect: (customer: CustomerRef) => void;
  placeholder?: string;
  className?: string;
}

const norm = (s?: string) => (s || '').trim().toLowerCase();

// A type-ahead customer picker for confirming the target of an employee-
// originated thread (#7). Mirrors UserSearchSelect: the results list renders in a
// PORTAL on document.body with fixed positioning so it is never clipped by the
// conversation card's overflow-hidden, and it closes on outside-click / scroll /
// Escape. Differences from UserSearchSelect: the source is a DEBOUNCED remote
// search (searchCustomers) rather than a one-shot directory load, and the AI's
// candidates are surfaced first as suggestions when the rep hasn't typed yet.
// The field is ALWAYS editable — a human can override the AI at any time.
const CustomerConfirmSelect: React.FC<Props> = ({ value, candidates, onSelect, placeholder, className }) => {
  // The input mirrors the resolved value's name when present; the rep can type
  // over it to search/override. Kept in sync with `value` on refresh (below).
  const [q, setQ] = useState(value?.name || '');
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const [results, setResults] = useState<CustomerRef[]>([]);
  const [loading, setLoading] = useState(false);
  // Tracks whether the rep has edited the input this session. Until they do, the
  // input follows `value` on prop refresh (so the AI's re-attribution re-fills
  // the field). Once they're actively typing, we don't clobber their query.
  const [dirty, setDirty] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // The dropdown is portaled into document.body, so it is NOT a DOM descendant of
  // wrapRef. Track it separately so outside-click / scroll-close treat clicks
  // inside the dropdown as "inside" (same fix as UserSearchSelect).
  const portalRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-fill (#7): when the resolved value changes underneath us (the poll /
  // SignalR refresh re-attributes the message after the rep answers the AI's
  // "which customer?" SMS), reflect the new target in the input — UNLESS the rep
  // is mid-edit. This is what makes the dropdown re-fill from props on refresh.
  useEffect(() => {
    if (!dirty) setQ(value?.name || '');
  }, [value?.id, value?.name, dirty]);

  const place = () => {
    const r = inputRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 240) });
  };

  useEffect(() => {
    if (!open) return;
    place();
    const inside = (t: EventTarget | null) =>
      (t instanceof Node) &&
      (!!wrapRef.current?.contains(t) || !!portalRef.current?.contains(t));
    const onScroll = (e: Event) => { if (!inside(e.target)) setOpen(false); else place(); };
    const onDocClick = (e: MouseEvent) => { if (!inside(e.target)) setOpen(false); };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    document.addEventListener('mousedown', onDocClick);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('mousedown', onDocClick);
    };
  }, [open]);

  // Debounced remote search as the rep types. searchCustomers is non-fatal (it
  // returns [] on failure/short query), so this never throws into the UI.
  useEffect(() => {
    if (!dirty) return; // only search on real typing, not on the seeded value
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const term = q.trim();
    if (term.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      searchCustomers(term)
        .then((list) => setResults(list))
        .finally(() => setLoading(false));
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q, dirty]);

  const pick = (c: CustomerRef) => {
    onSelect(c);
    setQ(c.name);
    setDirty(false);
    setResults([]);
    setOpen(false);
  };

  const cand = candidates || [];
  // Before the rep types (not dirty), suggest the AI's candidates. Once they're
  // typing, show live search results instead. Either way the input is editable.
  const showCandidates = !dirty && cand.length > 0;
  const ql = norm(q);
  // Filter candidates by the seeded text too, so they stay relevant.
  const candMatches = showCandidates
    ? cand.filter((c) => !ql || norm(c.name).includes(ql))
    : [];
  const list = showCandidates ? candMatches : results;

  return (
    <div ref={wrapRef} className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <input
        ref={inputRef}
        type="text"
        value={q}
        onChange={(e) => { setQ(e.target.value); setDirty(true); setOpen(true); place(); }}
        onFocus={() => { setOpen(true); place(); }}
        onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
        placeholder={placeholder}
        className={className}
      />
      {open && pos && createPortal(
        <div
          ref={portalRef}
          onMouseDown={(e) => e.preventDefault()}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999 }}
          className="max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-xl text-left"
        >
          {showCandidates && candMatches.length > 0 && (
            <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 bg-gray-50">
              Suggested by AI
            </div>
          )}
          {loading ? (
            <div className="px-3 py-2 text-xs text-gray-400">Searching…</div>
          ) : list.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-400">
              {q.trim().length < 2 ? 'Type to search customers…' : 'No matches'}
            </div>
          ) : list.map((c, i) => (
            <button
              key={c.id || c.name || i}
              type="button"
              onClick={() => pick(c)}
              className="block w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-brand-50 truncate"
              title={c.name}
            >
              {c.name}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default CustomerConfirmSelect;
